import { ReactElement, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Input, Label, Paragraph, Spinner, ValidationMessage } from '@digdir/designsystemet-react';
import { searchCompanies, type ChosenCompany } from '../../api/signup';
import { cleanOrgNumber, formatOrgNumber, missingDigits, wrongCheckDigit } from './signUpView';
import classes from './SignUp.module.css';

/** Waits until the user stops typing before asking the register */
const useDebounced = (value: string, ms: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
};

type Props = {
  id: string;
  company: ChosenCompany | null;
  onChange: (company: ChosenCompany | null) => void;
  error?: string;
};

/**
 * The company by org number or name from Enhetsregisteret. A company that is not there can be
 * used by its name, and gets its number later.
 */
export const CompanyPicker = ({ id, company, onChange, error }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const asked = useDebounced(query.trim(), 300);
  const digits = missingDigits(query);
  // Nine digits with a wrong check digit are not looked up: the user is told to check the number
  const badNumber = wrongCheckDigit(query);
  const searching = asked.length >= 2 && digits === null && !wrongCheckDigit(asked);
  const { data: hits = [], isFetching, isError } = useQuery({
    queryKey: ['signup', 'companies', asked],
    queryFn: () => searchCompanies(asked),
    enabled: searching,
    staleTime: 60_000,
    retry: false,
  });
  const isNumber = /^\d{9}$/.test(cleanOrgNumber(asked));
  const choose = (hit: (typeof hits)[number]) =>
    onChange({
      source: 'brreg',
      orgNumber: hit.orgNumber,
      name: hit.name,
      postalPlace: hit.postalPlace,
      employees: hit.employees,
    });
  // A whole org number with one company that can sign up is the choice, without a click
  const only = isNumber && !isFetching && hits.length === 1 && !hits[0].taken ? hits[0] : null;
  useEffect(() => {
    if (only && !company) choose(only);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only?.orgNumber]);
  const takenNumber = isNumber && !isFetching && hits.length === 1 && hits[0].taken;
  const K = 'auth.signUp.companyPicker';
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  if (company)
    return (
      <div className={classes.chosen} id={id} tabIndex={-1} aria-describedby={error ? errorId : undefined}>
        <div className={classes.chosenText}>
          <span className="ds-label" data-size="sm">
            {company.name}
          </span>
          <Paragraph data-size="sm" className={classes.hint}>
            {company.source === 'brreg'
              ? [
                  `${t(`${K}.orgNumber`)} ${formatOrgNumber(company.orgNumber)}`,
                  company.postalPlace,
                  company.employees !== null ? t(`${K}.employees`, { count: company.employees, number: company.employees.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'nb-NO') }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : t(`${K}.manualNote`)}
          </Paragraph>
          {error && <ValidationMessage id={errorId}>{error}</ValidationMessage>}
        </div>
        <Button
          type="button"
          variant="tertiary"
          data-size="sm"
          onClick={() => {
            onChange(null);
            setTimeout(() => document.getElementById(id)?.focus(), 0);
          }}
        >
          {t(`${K}.change`)}
        </Button>
      </div>
    );

  const showNoHits = searching && !isFetching && !isError && asked === query.trim() && hits.length === 0 && asked.length >= 3;

  return (
    <div className={classes.picker}>
      <Field>
        <Label htmlFor={id}>{t('auth.signUp.fields.orgNumber')}</Label>
        <Paragraph data-size="sm" id={hintId} className={classes.hint}>
          {t('auth.signUp.orgNumberHint')}
        </Paragraph>
        <Input
          id={id}
          inputMode="search"
          autoComplete="off"
          value={query}
          aria-invalid={!!error}
          aria-describedby={[hintId, error ? errorId : ''].filter(Boolean).join(' ')}
          onChange={(event) => setQuery(event.target.value)}
        />
        {error && <ValidationMessage id={errorId}>{error}</ValidationMessage>}
      </Field>
      <div aria-live="polite" className={classes.results}>
        {digits !== null && (
          <Paragraph data-size="sm" className={classes.hint}>
            {t(`${K}.digits`, { count: digits })}
          </Paragraph>
        )}
        {badNumber && <Paragraph data-size="sm">{t(`${K}.checkDigit`)}</Paragraph>}
        {isFetching && (
          <Paragraph data-size="sm" className={classes.hint}>
            <Spinner aria-hidden data-size="xs" /> {t(`${K}.searching`)}
          </Paragraph>
        )}
        {hits.length > 0 && (
          <ul className={classes.hits} aria-label={t(`${K}.results`)}>
            {hits.map((hit) => (
              <li key={hit.orgNumber}>
                <button
                  type="button"
                  className={classes.hit}
                  disabled={hit.taken}
                  onClick={() => choose(hit)}
                >
                  <span className="ds-label" data-size="sm">
                    {hit.name}
                  </span>
                  <span className={classes.hint}>
                    {[`${t(`${K}.orgNumber`)} ${formatOrgNumber(hit.orgNumber)}`, hit.postalPlace].filter(Boolean).join(' · ')}
                    {hit.taken && ` · ${t(`${K}.taken`)}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {takenNumber && (
          <Alert data-color="warning" data-size="sm">
            <Paragraph data-size="sm">{t(`${K}.takenAlert`)}</Paragraph>
          </Alert>
        )}
        {isError && (
          <Alert data-color="warning" data-size="sm">
            <Paragraph data-size="sm">{t(`${K}.registryDown`)}</Paragraph>
          </Alert>
        )}
        {showNoHits && isNumber && <Paragraph data-size="sm">{t(`${K}.noNumber`)}</Paragraph>}
        {((showNoHits && !isNumber) || (isError && !isNumber && asked.length >= 2)) && (
          <div className={classes.noHits}>
            <Paragraph data-size="sm">{t(`${K}.noHits`)}</Paragraph>
            <Button type="button" variant="secondary" data-size="sm" onClick={() => onChange({ source: 'manual', name: asked })}>
              {t(`${K}.useName`, { name: asked })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
